import * as React from 'react'
import { isNodeSelection } from '@tiptap/react'
import type { Editor } from '@tiptap/react'
// --- Hooks ---
import { useTiptapEditor } from 'src/hooks/use-tiptap-editor'
import { Button, ButtonProps } from '@components/SAPPEditor/tiptap-ui-primitive/button'
import { getFormattedHeadingName, HeadingButton, Level } from '../heading-button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from '@components/SAPPEditor/tiptap-ui-primitive/dropdown-menu'
import { ChevronDownIcon } from '../../tiptap-icons/chevron-down-icon'
import { canSetTextAlign, checkTextAlignExtension, isTextAlignActive, shouldShowTextAlignButton, TextAlign, TextAlignButton, textAlignIcons } from '../text-align-button'
import { AlignLeftIcon } from '../../tiptap-icons/align-left-icon'
import { AlignCenterIcon } from '../../tiptap-icons/align-center-icon'
import { AlignRightIcon } from '../../tiptap-icons/align-right-icon'
import { AlignJustifyIcon } from '../../tiptap-icons/align-justify-icon'

export interface TextAlignDropdownMenuProps extends Omit<ButtonProps, 'type'> {
  editor?: Editor | null
  listTypes?: TextAlign[]
  hideWhenUnavailable?: boolean
}

export function TextAlignDropdownMenu({
  editor: providedEditor,
  listTypes = ['left', 'center', 'right', 'justify'],
  hideWhenUnavailable = false,
  ...props
}: TextAlignDropdownMenuProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const editor = useTiptapEditor(providedEditor)
  const handleOnOpenChange = (open: boolean) => {
      setIsOpen(open)
    }

  const getActiveIcon = React.useCallback(() => {
    if (!editor) return <AlignLeftIcon className="tiptap-button-icon" />
    const activeAlign = listTypes.find((align) =>
      editor.isActive('paragraph', { textAlign: align }),
    ) as TextAlign | undefined
    switch (activeAlign) {
      case 'left':
        return <AlignLeftIcon className="tiptap-button-icon" />
      case 'center':
        return <AlignCenterIcon className="tiptap-button-icon" />
      case 'right':
        return <AlignRightIcon className="tiptap-button-icon" />
      case 'justify':
        return <AlignJustifyIcon className="tiptap-button-icon" />
      default:
        return <AlignLeftIcon className="tiptap-button-icon" />
    }
  }, [editor, listTypes])

  const canToggleAnyAlign = React.useCallback((): boolean => {
    if (!editor) return false
    return listTypes.some((align) => canSetTextAlign(editor, align, true))
  }, [editor, listTypes])
  const isAnyAlignActive = React.useMemo((): boolean => {
    if (!editor) return false
    return listTypes.some((align) => isTextAlignActive(editor, align))
  }, [editor, listTypes])


  const isDisabled = !canToggleAnyAlign()

  const shouldShow = React.useMemo(
    () => shouldShowTextAlignButton(editor, !isDisabled, hideWhenUnavailable),
    [editor, isDisabled, hideWhenUnavailable],
  )

  if (!shouldShow || !editor || !editor.isEditable) {
    return null
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOnOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          disabled={isDisabled}
          data-style="ghost"
          data-active-state={isAnyAlignActive ? 'on' : 'off'}
          data-disabled={isDisabled}
          role="button"
          tabIndex={-1}
          aria-label="Text Align"
          aria-pressed={isAnyAlignActive}
          tooltip=""
          data-size="large"
          {...props}
        >
          {getActiveIcon()}
          <ChevronDownIcon width={'16px'} height={'16px'} className="tiptap-button-dropdown-small" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent>
        <DropdownMenuGroup className='align-dropdown-menu'>
          {listTypes.map((align) => (
            <DropdownMenuItem key={`heading-${align}`} asChild>
              <TextAlignButton
                align={align}
              />
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default TextAlignDropdownMenu
